/**
 * RestoreWizard.jsx
 *
 * Multi-step guided restore dialog for database backups.
 * Supports both stored backups (by ID) and file uploads.
 *
 * Steps:
 *   1. Preview , show backup contents, compare with current DB
 *   2. Confirm , explain consequences, checkbox acknowledgement
 *   3. Progress, restore in progress with spinner
 *   4. Complete, show results, link to undo via restore point
 */

import React, { useState, useEffect } from 'react';
import { Button } from '../ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '../ui/dialog';
import { Badge } from '../ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../ui/table';
import {
  Loader2, RotateCcw, ShieldCheck, AlertTriangle,
  CheckCircle2, ArrowRight, ArrowLeft, Database,
} from 'lucide-react';
import { toast } from 'sonner';
import { previewRestore, restoreBackup, uploadPreview, restoreFromFile } from '../../lib/api';

export function RestoreWizard({ open, onOpenChange, backup, file, onRestoreComplete }) {
  const [step, setStep] = useState(1);
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [restoring, setRestoring] = useState(false);
  const [confirmed, setConfirmed] = useState(false);
  const [result, setResult] = useState(null);

  const isFileRestore = !backup && !!file;
  const label = backup?.name || file?.name || 'Unknown';

  // Reset on open
  useEffect(() => {
    if (open && (backup || file)) {
      setStep(1);
      setPreview(null);
      setLoading(true);
      setConfirmed(false);
      setResult(null);
      loadPreview();
    }
  }, [open, backup?.id, file]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadPreview = async () => {
    try {
      if (isFileRestore) {
        const { data } = await uploadPreview(file);
        setPreview(data);
      } else if (backup) {
        const { data } = await previewRestore(backup.id);
        setPreview(data);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to load restore preview');
      onOpenChange(false);
    } finally {
      setLoading(false);
    }
  };

  const handleRestore = async () => {
    setRestoring(true);
    setStep(3);
    try {
      let data;
      if (isFileRestore) {
        const res = await restoreFromFile(file);
        data = res.data;
      } else {
        const res = await restoreBackup(backup.id, { confirm: true });
        data = res.data;
      }
      setResult(data);
      setStep(4);
      toast.success('Database restored successfully');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Restore failed');
      setStep(2);
    } finally {
      setRestoring(false);
    }
  };

  const formatBytes = (bytes) => {
    if (!bytes) return '0 B';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!restoring) onOpenChange(v); }}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <RotateCcw className="w-5 h-5" />
            Restore Database
          </DialogTitle>
          <DialogDescription>
            {step === 1 && 'Review what this backup contains before restoring.'}
            {step === 2 && 'Please confirm you want to proceed with the restore.'}
            {step === 3 && 'Restoring your database, please wait...'}
            {step === 4 && 'Restore completed successfully.'}
          </DialogDescription>
        </DialogHeader>

        {/* Step indicators */}
        <div className="flex items-center justify-center gap-2 py-2" data-testid="restore-wizard-steps">
          {[1, 2, 3, 4].map((s) => (
            <div key={s} className="flex items-center gap-2">
              <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-medium border-2 transition-colors ${
                s < step ? 'bg-emerald-100 border-emerald-500 text-emerald-700'
                  : s === step ? 'bg-primary text-primary-foreground border-primary'
                  : 'bg-muted border-muted-foreground/30 text-muted-foreground'
              }`}>
                {s < step ? <CheckCircle2 className="w-4 h-4" /> : s}
              </div>
              {s < 4 && <div className={`w-8 h-0.5 ${s < step ? 'bg-emerald-500' : 'bg-muted-foreground/20'}`} />}
            </div>
          ))}
        </div>

        {/* Step 1: Preview */}
        {step === 1 && (
          <div className="space-y-4" data-testid="restore-step-preview">
            {loading ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="w-6 h-6 animate-spin mr-2" /> Loading preview...
              </div>
            ) : preview ? (
              <>
                {/* Backup info */}
                <div className="p-3 rounded-lg bg-muted/50 border space-y-1 text-sm">
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Source</span>
                    <span className="font-medium">{isFileRestore ? file.name : preview.backup_name}</span>
                  </div>
                  {preview.backup_date && (
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Created</span>
                      <span className="font-mono text-xs">{preview.backup_date?.slice(0, 19).replace('T', ' ')}</span>
                    </div>
                  )}
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Size</span>
                    <span>{formatBytes(preview.backup_size)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Collections</span>
                    <span>{preview.total_collections}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Total documents in backup</span>
                    <span className="font-medium">{preview.total_backup_docs}</span>
                  </div>
                </div>

                {/* Collection comparison table */}
                <div className="rounded-md border overflow-auto max-h-[300px]">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Collection</TableHead>
                        <TableHead className="text-right">Current</TableHead>
                        <TableHead className="text-right">In Backup</TableHead>
                        <TableHead className="text-right">Change</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {preview.comparison.map((c) => (
                        <TableRow key={c.collection}>
                          <TableCell className="font-mono text-xs">{c.collection}</TableCell>
                          <TableCell className="text-right text-sm">{c.current_count}</TableCell>
                          <TableCell className="text-right text-sm">{c.backup_count}</TableCell>
                          <TableCell className="text-right">
                            {c.diff === 0 ? (
                              <span className="text-muted-foreground text-xs">same</span>
                            ) : (
                              <Badge variant={c.diff > 0 ? 'default' : 'secondary'} className="text-xs">
                                {c.diff > 0 ? '+' : ''}{c.diff}
                              </Badge>
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </>
            ) : null}
          </div>
        )}

        {/* Step 2: Confirm */}
        {step === 2 && (
          <div className="space-y-4" data-testid="restore-step-confirm">
            <div className="p-4 rounded-lg bg-amber-50 border border-amber-200 space-y-3">
              <div className="flex items-start gap-2">
                <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                <div className="text-sm space-y-2">
                  <p className="font-semibold text-amber-800">What will happen:</p>
                  <ul className="list-disc pl-4 space-y-1 text-amber-700">
                    <li>A <strong>safety restore point</strong> of your current data will be created automatically</li>
                    <li>Each document in the backup will <strong>overwrite</strong> its matching record in the database (matched by ID)</li>
                    <li>New documents from the backup that don't exist yet will be <strong>inserted</strong></li>
                    <li>Documents that exist in your current database but are <strong>not in the backup will remain unchanged</strong></li>
                    <li>No duplicate entries will be created</li>
                  </ul>
                </div>
              </div>
            </div>

            <div className="p-3 rounded-lg bg-emerald-50 border border-emerald-200">
              <div className="flex items-start gap-2">
                <ShieldCheck className="w-5 h-5 text-emerald-600 flex-shrink-0 mt-0.5" />
                <p className="text-sm text-emerald-700">
                  <strong>Safety net:</strong> A complete snapshot of your current database will be saved before any changes are made.
                  You can undo the restore at any time by restoring from that snapshot.
                </p>
              </div>
            </div>

            <label className="flex items-center gap-3 p-3 rounded-lg border cursor-pointer hover:bg-muted/30">
              <input
                type="checkbox"
                checked={confirmed}
                onChange={(e) => setConfirmed(e.target.checked)}
                className="h-4 w-4"
                data-testid="restore-confirm-checkbox"
              />
              <span className="text-sm">
                I understand that this will overwrite existing data with the backup contents.
              </span>
            </label>
          </div>
        )}

        {/* Step 3: Progress */}
        {step === 3 && (
          <div className="flex flex-col items-center justify-center py-10 space-y-4" data-testid="restore-step-progress">
            <Loader2 className="w-10 h-10 animate-spin text-primary" />
            <div className="text-center">
              <p className="font-medium">Restoring database...</p>
              <p className="text-sm text-muted-foreground mt-1">Creating safety restore point and applying backup data. Do not close this window.</p>
            </div>
          </div>
        )}

        {/* Step 4: Complete */}
        {step === 4 && result && (
          <div className="space-y-4" data-testid="restore-step-complete">
            <div className="flex flex-col items-center py-4">
              <CheckCircle2 className="w-12 h-12 text-emerald-500 mb-3" />
              <h3 className="font-semibold text-lg">Restore Complete</h3>
              {result.restored_from && (
                <p className="text-sm text-muted-foreground mt-1">
                  Restored from: {result.restored_from?.slice(0, 19).replace('T', ' ')}
                </p>
              )}
              {result.filename && (
                <p className="text-sm text-muted-foreground mt-1">
                  Source file: {result.filename}
                </p>
              )}
            </div>

            {/* Restore counts */}
            <div className="rounded-md border overflow-auto max-h-[250px]">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Collection</TableHead>
                    <TableHead className="text-right">Documents Restored</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {Object.entries(result.restored_counts || {}).sort().map(([coll, count]) => (
                    <TableRow key={coll}>
                      <TableCell className="font-mono text-xs">{coll}</TableCell>
                      <TableCell className="text-right text-sm font-medium">{count}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>

            <div className="p-3 rounded-lg bg-sky-50 border border-sky-200 text-sm text-sky-700">
              <Database className="w-4 h-4 inline mr-1" />
              <strong>Undo available:</strong> A safety restore point was saved before the restore.
              If anything looks wrong, find it in the backups list and restore from it.
            </div>
          </div>
        )}

        {/* Footer navigation */}
        <DialogFooter className="flex justify-between sm:justify-between">
          {step === 1 && (
            <>
              <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="restore-cancel-btn">Cancel</Button>
              <Button onClick={() => setStep(2)} disabled={!preview} data-testid="restore-next-btn">
                Next: Confirm <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </>
          )}
          {step === 2 && (
            <>
              <Button variant="outline" onClick={() => setStep(1)} data-testid="restore-back-btn">
                <ArrowLeft className="w-4 h-4 mr-2" /> Back
              </Button>
              <Button onClick={handleRestore} disabled={!confirmed || restoring} variant="destructive" data-testid="restore-execute-btn">
                {restoring ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <RotateCcw className="w-4 h-4 mr-2" />}
                Restore Now
              </Button>
            </>
          )}
          {step === 4 && (
            <Button className="ml-auto" onClick={() => { onOpenChange(false); if (onRestoreComplete) onRestoreComplete(); }} data-testid="restore-done-btn">
              Done
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
